// ================================================================
// 🔄 CHURN RATE REPORT - Relatório de Churn Rate
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const ChurnReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [churnData, setChurnData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsGridColor = useChartsColors();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: {
          color: chartsGridColor
        }
      },
      y: {
        grid: {
          color: chartsGridColor
        },
      }
    }
  };

  const loadChurnReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Carregando Relatório de Churn Rate com filtros...', filtersToUse);

      const result = await ReportsService.getChurnReport({
        period: filtersToUse.period
      });

      if (result.success) {
        setChurnData(result.data);
        console.log('✅ Relatório de Churn Rate carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChurnReport = useCallback(async () => {
    loadChurnReportWithFilters(filters);
  }, [filters, loadChurnReportWithFilters]);

  useEffect(() => {
    loadChurnReport();
  }, [loadChurnReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadChurnReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadChurnReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const monthlyRetentionData = churnData?.charts?.monthlyRetention ? {
    labels: churnData.charts.monthlyRetention.map(item => new Date(item.month).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })),
    datasets: [{
      label: 'Taxa de Retenção (%)',
      data: churnData.charts.monthlyRetention.map(item => item.retention_rate),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const churnTrendData = churnData?.charts?.churnTrend ? {
    labels: churnData.charts.churnTrend.map(item => new Date(item.month).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })),
    datasets: [{
      label: 'Clientes Ativos',
      data: churnData.charts.churnTrend.map(item => item.active_customers),
      borderColor: '#007bff',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }, {
      label: 'Clientes Perdidos',
      data: churnData.charts.churnTrend.map(item => item.churned_customers),
      borderColor: '#dc3545',
      backgroundColor: 'rgba(220, 53, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:account-minus" style={{ fontSize: '22px', color: '#dc3545' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Churn Rate
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadChurnReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadChurnReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados de churn...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && churnData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={churnData.kpis.churn_rate.label}
                  value={churnData.kpis.churn_rate.formatted}
                  icon="mdi:trending-down"
                  color="#dc3545"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={churnData.kpis.retention_rate.label}
                  value={churnData.kpis.retention_rate.formatted}
                  icon="mdi:trending-up"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={churnData.kpis.churned_customers.label}
                  value={churnData.kpis.churned_customers.value}
                  icon="mdi:account-minus"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={churnData.kpis.total_customers.label}
                  value={churnData.kpis.total_customers.value}
                  icon="mdi:account-group"
                  color="#17a2b8"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Taxa de Retenção Mensal
                    </span>
                    <Line data={monthlyRetentionData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Churn
                    </span>
                    <Line data={churnTrendData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default ChurnReport;

